/**
 * useWebDialerRegistration — keeps this browser tab's inbound-call registration in sync with the
 * org's routing config, with zero action needed from the user.
 *
 * On SignalR connect (auth + company context are ready by then) and on every InboundRoutingChanged
 * broadcast (org-wide), it re-asks the backend "am I a ring target?" and enables/disables the
 * Telnyx web manager's receive mode accordingly. An admin flipping the quick-switch therefore
 * starts/stops a secretary's browser ringing within seconds.
 *
 * Desktop web only: receive mode itself no-ops on native, and mobile browsers never register.
 * Errors (403 without voip.call, transient network) are treated as "not a target".
 */

import { useEffect } from 'react';
import * as signalR from '@microsoft/signalr';
import { Capacitor } from '@capacitor/core';
import { apiService } from '@/lib/api';
import { voiceManager } from '@/lib/voiceManager';
import { telnyxVoiceWebManager } from '@/lib/telnyxVoiceWeb';

/**
 * Desktop web only (review fix): native is excluded by design, and mobile BROWSERS must not
 * register either — they'd burn session slots and ring devices with no usable call UI (the web
 * softphone is desktop-only throughout). "Desktop" = reasonably wide viewport with a fine primary
 * pointer. Evaluated once per sync; a mid-session window resize doesn't flip registration.
 * Exported for the quick-switch widget, which shares the same gate.
 */
export function isDesktopWeb(): boolean {
  if (Capacitor.isNativePlatform()) return false;
  try {
    return window.matchMedia('(min-width: 768px) and (pointer: fine)').matches;
  } catch {
    return false;
  }
}

/**
 * Bumped whenever the auth/org context tears down. A `getInboundRoutingMe` answer that raced a
 * context switch must not enable/disable the NEW context's registration (review-4 fix).
 */
let syncEpoch = 0;

/**
 * Monotonic sequence within a context (review-6 #6): two rapid InboundRoutingChanged events
 * (target → untarget) start overlapping syncs, and without ordering the OLDER response could
 * apply last — re-registering a just-untargeted user until the next periodic re-sync. Only the
 * newest in-flight sync is allowed to apply its answer.
 */
let syncSeq = 0;

/**
 * Periodic reconciliation cadence (review-5 fix): InboundRoutingChanged is fire-and-forget on the
 * backend, so a lost broadcast (Realtime restart, dropped group message) could otherwise leave a
 * newly targeted user unregistered — or an un-targeted one registered — until the next full page
 * load. One trivial indexed GET per tab per interval.
 */
const RESYNC_INTERVAL_MS = 5 * 60_000;

/** Latch so a DISMISSED notification prompt isn't re-asked on every sync (see syncRegistration). */
const NOTIFICATION_ASK_KEY = 'webdialer.notificationAsked';

async function syncRegistration(): Promise<void> {
  const epoch = syncEpoch;
  const seq = ++syncSeq;
  try {
    const { isRingTarget } = await apiService.getInboundRoutingMe();
    if (epoch !== syncEpoch) return; // stale: answered for a previous login/org context
    if (seq !== syncSeq) return; // superseded: a newer sync is in flight — let it decide
    if (isRingTarget) {
      // Ask for notification permission up front (while it's a deliberate app feature, not a
      // surprise mid-ring). Denials are fine — the in-app card + title flash still alert.
      // Asked ONCE per browser profile: dismissing Chrome's prompt leaves permission on 'default',
      // so without the latch every sync/page load re-prompts. Allow/Block need no latch (they
      // move permission off 'default'); the flag exists purely for the dismissed case.
      if (typeof Notification !== 'undefined' && Notification.permission === 'default'
          && !localStorage.getItem(NOTIFICATION_ASK_KEY)) {
        try {
          localStorage.setItem(NOTIFICATION_ASK_KEY, '1');
          void Notification.requestPermission();
        } catch { /* ignore */ }
      }
      // Through the facade, NOT telnyxVoiceWebManager directly: VoiceCallProvider listens to the
      // facade, which must resolve + bridge the web backend BEFORE the first invite arrives, or
      // the tab rings the SDK with no answer UI (review fix).
      await voiceManager.enableInboundReceive();
    } else {
      await voiceManager.disableInboundReceive();
    }
  } catch (err: any) {
    if (epoch !== syncEpoch || seq !== syncSeq) return;
    // Transient failures (network / 5xx) must not deregister a working receiver. But a definitive
    // 401/403 means auth or the voip.call permission was REVOKED — keeping the registration would
    // let a de-permissioned user receive calls until the liveness window expired (round-3 review).
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      try { await voiceManager.disableInboundReceive(); } catch { /* best effort */ }
    }
  }
}

/** Manual re-sync trigger for UI actions (e.g. the indicator menu's "Resume receiving"). */
export function resyncWebDialerRegistration(): void {
  void syncRegistration();
}

export function useWebDialerRegistration(
  connection: signalR.HubConnection | null,
  status: 'disconnected' | 'connecting' | 'connected' | 'reconnecting',
): void {
  // Initial + reconnect sync. Keyed on 'connected' so the check runs after login and company
  // selection are settled (the SignalR connection is only established then), and re-runs after
  // network drops (the config may have changed while offline).
  useEffect(() => {
    if (!isDesktopWeb()) return;
    if (status !== 'connected') return;
    void syncRegistration();
  }, [status]);

  // Periodic reconciliation, INDEPENDENT of the SignalR status (review-8 #6a): the re-sync used
  // to run only while 'connected', so a Realtime outage — during which InboundRoutingChanged
  // can't arrive either — silenced every recovery channel at once. The REST API works without
  // the socket; the epoch/sequence guards inside syncRegistration handle any context races, and
  // an unauthenticated early tick just fails harmlessly (401 → disable, a no-op while off).
  // One IMMEDIATE sync at mount too (review-9 #6): the connected-keyed sync waits for SignalR
  // and the timer first fires at +5min, so a Realtime outage at page load would otherwise delay
  // the initial registration by five minutes.
  useEffect(() => {
    if (!isDesktopWeb()) return;
    void syncRegistration();
    const timer = setInterval(() => { void syncRegistration(); }, RESYNC_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  // Live config changes — and, critically, teardown. The cleanup runs when the connection is
  // replaced (org switch) or the provider unmounts (LOGOUT): the manager is a singleton that
  // outlives React, so without this the previous account's registration keeps ringing over the
  // login screen — or for the next signed-in user — until the liveness window expires (round-3
  // review critical). destroy(), not just disable (review-4): it invalidates in-flight backend
  // resolution (which could otherwise resurrect receive mode post-logout), resets the cached
  // provider (a Twilio↔Telnyx org switch must re-probe), and clears the cached token/identity so
  // the next user can never dial — or be recorded — as the previous one. The next connection's
  // 'connected' status re-registers under the new context.
  useEffect(() => {
    if (!connection || !isDesktopWeb()) return;
    const handler = () => { void syncRegistration(); };
    // Admin kick: delivered to the user:{userId} group, so "is this me?" is implicit — EVERY tab
    // of the kicked user latches receive mode off (until reload or an auth-context change),
    // including cap-blocked tabs with no session and tabs whose session id was just replaced by
    // a self-heal (review-6 #2: session-id matching missed both, and the survivors re-registered
    // within a minute, silently undoing the kick). A multi-org user's tabs signed into a
    // DIFFERENT org ignore it via org_id.
    const kickHandler = (payload: {
      org_id?: string; user_id?: string; session_ids?: string[]; kept_session_id?: string | null;
    }) => {
      const myOrgId = apiService.getOrgId();
      if (payload?.org_id && myOrgId && payload.org_id.toLowerCase() !== myOrgId.toLowerCase()) return;
      // Self "disconnect my other tabs": the INITIATING tab's session rides along as
      // kept_session_id and must not latch itself off. Every other tab of the user — including
      // cap-blocked ones with no session id — stands down as with an admin kick. (A self-heal
      // replacing this tab's session id between the POST and this event would latch it too;
      // rare, and a reload recovers.)
      const kept = payload?.kept_session_id;
      const mySessionId = telnyxVoiceWebManager.currentReceiveSessionId;
      if (kept && mySessionId && kept.toLowerCase() === mySessionId.toLowerCase()) return;
      void voiceManager.remoteDisconnectInboundReceive();
    };
    connection.on('InboundRoutingChanged', handler);
    connection.on('WebDialerSessionsEnded', kickHandler);
    return () => {
      connection.off('InboundRoutingChanged', handler);
      connection.off('WebDialerSessionsEnded', kickHandler);
      syncEpoch++;
      void voiceManager.destroy();
    };
  }, [connection]);
}
